/**
 * SalesLeadDetailPage — Phase 33.9 Sales Brain lead detail view.
 *
 * Fetches a single lead by ID and renders all fields with status badge,
 * contact links, and discovery metadata.
 */

import { Navigate, Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { isOmegaAdmin } from '@/features/omega/adminMode';
import { Breadcrumbs } from '@/features/omega/Breadcrumbs';
import { apiBaseUrl } from '@/api/http';
import { getAccessToken } from '@/api/supabase';

// ── Types ────────────────────────────────────────────────────────────────────

interface LeadSummary {
  id: string;
  first_name: string;
  last_name: string;
  title: string;
  company: string;
  company_size_estimate: number | null;
  industry: string;
  linkedin_url: string | null;
  email: string | null;
  discovery_source: string;
  discovery_signal: string;
  discovered_at: number;
  pipeline_id: string | null;
  status: string;
}

// ── Data ─────────────────────────────────────────────────────────────────────

async function fetchLead(id: string): Promise<LeadSummary> {
  const token = await getAccessToken();
  const resp = await fetch(`${apiBaseUrl()}/sales/leads/${id}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
  return resp.json() as Promise<LeadSummary>;
}

function useLead(id: string) {
  return useQuery({
    queryKey: ['sales', 'lead', id],
    queryFn: () => fetchLead(id),
  });
}

// ── Helpers (exported for tests) ─────────────────────────────────────────────

const STATUS_COLORS: Record<string, string> = {
  new:       '#7dd3fc',
  contacted: '#a78bfa',
  qualified: '#6ee7b7',
  proposed:  '#fbbf24',
  won:       '#34d399',
  lost:      '#f87171',
};

export function statusColor(s: string): string {
  return STATUS_COLORS[s] ?? 'var(--omega-ink-subtle)';
}

export function fmtTime(unix: number): string {
  return new Date(unix * 1000).toLocaleString('en-US', {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

export function formatCompanySize(n: number | null): string {
  if (n === null) return 'Unknown';
  if (n < 10)    return '< 10 employees';
  if (n < 50)    return '10–49 employees';
  if (n < 200)   return '50–199 employees';
  if (n < 1000)  return '200–999 employees';
  return '1000+ employees';
}

// ── Sub-components ────────────────────────────────────────────────────────────

function FieldRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div
      style={{
        display: 'flex',
        gap: 12,
        padding: '10px 0',
        borderTop: '1px solid var(--omega-rim, rgba(255,255,255,0.06))',
        alignItems: 'flex-start',
      }}
    >
      <span
        style={{
          flexShrink: 0,
          width: 160,
          fontSize: '8.5pt',
          color: 'var(--omega-ink-subtle)',
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          paddingTop: 2,
        }}
      >
        {label}
      </span>
      <span style={{ fontSize: '9.5pt', color: 'var(--omega-ink)' }}>
        {children}
      </span>
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function SalesLeadDetailPage() {
  if (!isOmegaAdmin()) return <Navigate to="/" replace />;

  const { id = '' } = useParams<{ id: string }>();
  const { data: lead, isPending, isError } = useLead(id);

  const breadcrumbLeaf = lead?.company || id.slice(0, 8);

  return (
    <div className="omega-shell" style={{ fontFamily: 'var(--omega-mono)', color: 'var(--omega-ink)', padding: '16px 20px' }}>
      <Breadcrumbs
        crumbs={[
          { label: 'OMEGA', to: '/omega' },
          { label: 'Sales', to: '/omega/sales' },
          { label: 'Leads', to: '/omega/sales/leads' },
          { label: breadcrumbLeaf },
        ]}
      />

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, marginTop: 8 }}>
        <h1 style={{ margin: 0, fontSize: '12pt', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
          {lead ? `${lead.first_name} ${lead.last_name}` : 'Lead Detail'}
        </h1>
        {lead && (
          <span
            style={{
              fontSize: '7.5pt',
              padding: '2px 8px',
              border: `1px solid ${statusColor(lead.status)}`,
              color: statusColor(lead.status),
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              fontWeight: 700,
              borderRadius: 3,
            }}
          >
            {lead.status}
          </span>
        )}
        <Link
          to="/omega/sales/leads"
          style={{
            marginLeft: 'auto',
            background: 'rgba(125,211,252,0.12)',
            border: '1px solid rgba(125,211,252,0.35)',
            borderRadius: 4,
            color: '#7dd3fc',
            fontFamily: 'var(--omega-mono)',
            fontSize: '8.5pt',
            fontWeight: 600,
            padding: '5px 12px',
            textDecoration: 'none',
            letterSpacing: '0.04em',
          }}
        >
          ← Back to Leads
        </Link>
      </div>

      {isPending && (
        <div style={{ color: 'var(--omega-ink-subtle)', fontSize: '9pt' }}>Loading…</div>
      )}
      {isError && (
        <div style={{ color: '#f87171', fontSize: '9pt' }}>Failed to load lead.</div>
      )}

      {lead && (
        <div
          style={{
            background: 'var(--omega-surface, rgba(255,255,255,0.04))',
            border: '1px solid var(--omega-rim, rgba(255,255,255,0.08))',
            borderRadius: 6,
            padding: '0 18px',
          }}
        >
          {/* Header block */}
          <div style={{ padding: '16px 0 14px', borderBottom: '1px solid var(--omega-rim, rgba(255,255,255,0.08))' }}>
            <div style={{ fontSize: '13pt', fontWeight: 700, color: 'var(--omega-ink)', marginBottom: 4 }}>
              {lead.first_name} {lead.last_name}
            </div>
            <div style={{ fontSize: '9.5pt', color: 'var(--omega-ink-subtle)' }}>
              {lead.title} · {lead.company}
            </div>
          </div>

          <FieldRow label="Status">
            <span
              style={{
                padding: '2px 8px',
                border: `1px solid ${statusColor(lead.status)}`,
                color: statusColor(lead.status),
                fontSize: '7.5pt',
                fontWeight: 700,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                borderRadius: 3,
              }}
            >
              {lead.status}
            </span>
          </FieldRow>

          <FieldRow label="Industry">{lead.industry}</FieldRow>

          <FieldRow label="Company Size">{formatCompanySize(lead.company_size_estimate)}</FieldRow>

          <FieldRow label="Discovery Source">{lead.discovery_source}</FieldRow>

          <FieldRow label="Discovery Signal">{lead.discovery_signal}</FieldRow>

          <FieldRow label="Email">
            {lead.email
              ? <a href={`mailto:${lead.email}`} style={{ color: '#7dd3fc', textDecoration: 'none' }}>{lead.email}</a>
              : '—'}
          </FieldRow>

          <FieldRow label="LinkedIn">
            {lead.linkedin_url
              ? <a href={lead.linkedin_url} target="_blank" rel="noopener noreferrer" style={{ color: '#7dd3fc', textDecoration: 'none' }}>{lead.linkedin_url}</a>
              : '—'}
          </FieldRow>

          <FieldRow label="Discovered">{fmtTime(lead.discovered_at)}</FieldRow>

          <FieldRow label="Pipeline ID">
            {lead.pipeline_id
              ? <span style={{ fontFamily: 'var(--omega-mono)', fontSize: '9pt' }}>{lead.pipeline_id}</span>
              : '—'}
          </FieldRow>
        </div>
      )}
    </div>
  );
}
